import React, { useState, useEffect } from 'react';
import { Save, Loader, ExternalLink, Mail, Key, Shield, AlertCircle } from 'lucide-react';
import { db } from '../../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const EmailServerSection = () => {
    const [credentials, setCredentials] = useState({
        serviceId: '',
        templateId: '',
        publicKey: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const docRef = doc(db, 'settings', 'emailService');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setCredentials(docSnap.data());
                }
            } catch (error) {
                console.error("Error fetching email settings:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCredentials(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await setDoc(doc(db, 'settings', 'emailService'), credentials);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (error) {
            console.error("Error saving settings:", error);
            alert("Failed to save settings. Please check your permissions.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Loading email settings...</div>;
    }

    return (
        <div className="panel active animate-in fade-in duration-500">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">System</div>
                    <h1>Email Server <span>Configuration</span></h1>
                </div>
            </div>

            <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column: Instructions */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <h3 className="font-bold text-[#20222D] mb-4 flex items-center gap-2">
                                <Mail size={18} className="text-[#A2CB52]" />
                                Setup Instructions
                            </h3>
                            <div className="space-y-4 text-sm text-gray-600">
                                <p>We use <strong>EmailJS</strong> to send notifications directly from your website without a backend server.</p>
                                <ol className="list-decimal pl-4 space-y-2">
                                    <li>Create a free account at <a href="https://www.emailjs.com/" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">EmailJS.com</a>.</li>
                                    <li>Add an <strong>Email Service</strong> (like Gmail).</li>
                                    <li>Create an <strong>Email Template</strong>.</li>
                                    <li>Copy your <strong>Service ID</strong>, <strong>Template ID</strong>, and <strong>Public Key</strong>.</li>
                                </ol>
                                <a
                                    href="https://dashboard.emailjs.com/"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 mt-4 text-[#A2CB52] hover:text-[#8db346] font-medium"
                                >
                                    <ExternalLink size={14} /> Open EmailJS Dashboard
                                </a>
                            </div>
                        </div>

                        <div className="bg-[#ECF5DC] p-6 rounded-2xl border border-[#A2CB52]/20">
                            <h3 className="font-bold text-[#20222D] mb-2 flex items-center gap-2">
                                <AlertCircle size={18} className="text-[#A2CB52]" />
                                Template Tips
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Use these variable names in your EmailJS template to capture form data:
                            </p>
                            <ul className="text-xs space-y-1 font-mono text-gray-700 bg-white/50 p-3 rounded-lg">
                                <li>{'{{to_email}}'} (Your generic email)</li>
                                <li>{'{{from_name}}'} (Lead Name)</li>
                                <li>{'{{from_email}}'} (Lead Email)</li>
                                <li>{'{{company}}'} (Company Name)</li>
                                <li>{'{{phone}}'} (Phone Number)</li>
                                <li>{'{{message}}'} (User Message)</li>
                            </ul>
                        </div>
                    </div>

                    {/* Right Column: Form */}
                    <div className="lg:col-span-2">
                        <form onSubmit={handleSave} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
                            {saved && (
                                <div className="absolute top-0 left-0 right-0 bg-[#A2CB52] text-white text-center py-2 text-sm font-bold animate-in slide-in-from-top-full">
                                    Settings Saved Successfully!
                                </div>
                            )}

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Service ID</label>
                                    <div className="relative">
                                        <Shield className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                        <input
                                            type="text"
                                            name="serviceId"
                                            value={credentials.serviceId}
                                            onChange={handleChange}
                                            placeholder="service_xxxxxxxx"
                                            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all font-mono text-sm"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Found in Email Services tab.</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Template ID</label>
                                    <div className="relative">
                                        <FileText className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                        <input
                                            type="text"
                                            name="templateId"
                                            value={credentials.templateId}
                                            onChange={handleChange}
                                            placeholder="template_xxxxxxxx"
                                            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all font-mono text-sm"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Found in Email Templates tab.</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Public Key</label>
                                    <div className="relative">
                                        <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                        <input
                                            type="text"
                                            name="publicKey"
                                            value={credentials.publicKey}
                                            onChange={handleChange}
                                            placeholder="user_xxxxxxxx"
                                            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A2CB52] focus:ring-2 focus:ring-[#A2CB52]/20 outline-none transition-all font-mono text-sm"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Found in Account Settings &gt; General &gt; API Public Key.</p>
                                </div>

                                <div className="pt-6 border-t border-gray-100 flex items-center justify-between">
                                    <p className="text-xs text-gray-400">
                                        Credentials are stored securely in your database.
                                    </p>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="bg-[#20222D] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#2a2d3d] transition-colors flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {saving ? (
                                            <>
                                                <Loader size={18} className="animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Save size={18} />
                                                Save Configuration
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper component for icon
const FileText = ({ size, className }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
);

export default EmailServerSection;
